import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getEvent, deleteEvent } from '../lib/firestore/events';
import { isEventCohost } from '../lib/firestore/eventCohosts';
import { getGroupMember } from '../lib/firestore/groups';
import { isAdmin } from '../lib/firestore/admins';
import '../styles/DeleteEvent.css';

const DeleteEvent = () => {
  const { eventId } = useParams();
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!eventId) {
      console.error('🔴 [DeleteEvent] Missing eventId');
      return;
    }
    
    // Wait for auth to finish loading before checking user
    if (authLoading) {
      console.log('🔵 [DeleteEvent] Auth still loading, waiting...');
      return;
    }
    
    // Only redirect if auth is done loading and there's no user
    if (!authLoading && !user) {
      console.log('🔵 [DeleteEvent] No user after auth loaded, redirecting to login');
      navigate('/login');
      return;
    }
    
    if (eventId && user && !authLoading) {
      loadEvent();
    }
  }, [eventId, user, authLoading, navigate]);

  const loadEvent = async () => {
    if (!eventId || !user) return;

    setLoading(true);
    try {
      const eventData = await getEvent(eventId);
      if (!eventData) {
        setError('Event not found');
        setLoading(false);
        return;
      }

      // Check if user can delete:
      // 1. User is the direct host (profile events)
      // 2. User created the event (createdBy field)
      // 3. User is an event cohost
      // 4. If event is hosted by a group: user is group owner/cohost
      const userIsHost = (eventData.hostId === user.uid && eventData.hostType === 'profile') || 
                         (eventData.hostId === user.uid && !eventData.hostType); // Fallback for old events
      const userIsCreator = eventData.createdBy === user.uid;
      const userIsCohost = await isEventCohost(eventId, user.uid);
      
      let userIsGroupOwnerOrCohost = false;
      if (eventData.hostType === 'group') {
        try {
          const member = await getGroupMember(eventData.hostId, user.uid);
          userIsGroupOwnerOrCohost = member?.role === 'owner' || member?.role === 'cohost';
        } catch (err) {
          console.warn('Error checking group membership:', err);
        }
      }
      
      const userIsAdmin = await isAdmin(user.uid);
      const canDelete = userIsHost || userIsCreator || userIsCohost || userIsGroupOwnerOrCohost || userIsAdmin;
      console.log('🔵 [DeleteEvent] Permission check:', {
        eventId,
        userId: user.uid,
        hostId: eventData.hostId,
        hostType: eventData.hostType,
        createdBy: eventData.createdBy,
        userIsHost,
        userIsCreator,
        userIsCohost,
        userIsGroupOwnerOrCohost,
        canDelete
      });
      
      if (!canDelete) {
        setError('Only the event creator, host, or cohost can delete this event');
        setLoading(false);
        return;
      }

      setEvent(eventData);
    } catch (err) {
      setError(err.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    console.log('🔵 [DeleteEvent] handleDelete called:', { hasEvent: !!event, confirmText, expected: 'DELETE', match: confirmText === 'DELETE' });
    
    if (!event || confirmText !== 'DELETE') {
      console.warn('🔴 [DeleteEvent] Delete validation failed:', { hasEvent: !!event, confirmText, expected: 'DELETE' });
      return;
    }

    console.log('🟢 [DeleteEvent] Validation passed, proceeding with delete...');
    setDeleting(true);
    setError('');

    try {
      console.log('🔵 [DeleteEvent] Attempting to delete event:', eventId);
      console.log('🔵 [DeleteEvent] Event data before delete:', {
        eventId,
        hostId: event?.hostId,
        hostType: event?.hostType,
        createdBy: event?.createdBy,
        userId: user.uid
      });
      await deleteEvent(eventId);
      console.log('🟢 [DeleteEvent] Event deleted successfully');

      // Navigate based on host type
      if (event.hostType === 'group') {
        navigate(`/groups/${event.hostId}`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      console.error('🔴 [DeleteEvent] Error deleting event:', err);
      console.error('🔴 [DeleteEvent] Error code:', err?.code);
      console.error('🔴 [DeleteEvent] Error message:', err?.message);
      const errorMessage = err?.message || 'Failed to delete event';
      // If it's a permission error, provide more helpful message
      if (errorMessage.includes('permission') || errorMessage.includes('Permission') || err?.code === 'permission-denied') {
        setError('Permission denied. You may not have permission to delete this event. Please check that you are the creator, host, cohost, or group owner/cohost.');
      } else {
        setError(errorMessage);
      }
      setDeleting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="delete-event-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="delete-event-container">
        <div className="error-message">{error || 'Event not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate(`/events/${eventId}`)}>
          Back to Event
        </button>
      </div>
    );
  }

  return (
    <div className="delete-event-container">
      <div className="delete-event-card">
        <header className="delete-header">
          <button className="btn-back" onClick={() => navigate(`/events/${eventId}`)}>
            ← Cancel
          </button>
          <h1>Delete Event</h1>
        </header>

        <div className="delete-warning">
          <div className="warning-icon">⚠️</div>
          <h2>This action cannot be undone</h2>
          <p>
            Deleting this event will permanently remove:
          </p>
          <ul className="delete-consequences">
            <li>All RSVPs and check-ins</li>
            <li>All event invitations</li>
            <li>All message boards</li>
            <li>Event history and data</li>
          </ul>
        </div>

        <div className="event-info-summary">
          <h3>{event.title}</h3>
          <p>{new Date(event.startsAt).toLocaleDateString()}</p>
        </div>

        <div className="delete-confirmation">
          <label htmlFor="confirmText">
            Type <strong style={{color: 'var(--error)', fontSize: '1.1em'}}>DELETE</strong> (all caps) to confirm:
          </label>
          <input
            type="text"
            id="confirmText"
            className="input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE here"
            style={{
              fontSize: '1.1rem',
              padding: '0.75rem',
              border: confirmText === 'DELETE' ? '2px solid var(--success)' : '2px solid var(--border-color)',
              backgroundColor: confirmText === 'DELETE' ? 'rgba(0, 255, 136, 0.1)' : 'var(--background-card)'
            }}
          />
          {confirmText && confirmText !== 'DELETE' && (
            <p style={{color: 'var(--error)', fontSize: '0.875rem', marginTop: '0.5rem'}}>
              ❌ Text does not match. Type exactly "DELETE" (all caps, no spaces)
            </p>
          )}
          {confirmText === 'DELETE' && (
            <p style={{color: 'var(--success)', fontSize: '0.875rem', marginTop: '0.5rem'}}>
              ✅ Confirmation text matches. You can now delete the event.
            </p>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="delete-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/events/${eventId}`)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={(e) => {
              console.log('🔵 [DeleteEvent] Delete button clicked:', { confirmText, disabled: deleting || confirmText !== 'DELETE' });
              e.preventDefault();
              handleDelete();
            }}
            disabled={deleting || confirmText !== 'DELETE'}
          >
            {deleting ? 'Deleting...' : 'Delete Event'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteEvent;

